import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	HOUSECALL_PRO_LOCATIONS_QUERY_KEY,
	invalidateIntegrationQuery,
	useDisconnectAccountFromIntegrationMutation,
	useGetExternalIntegrationQuery,
	usePostHousecallProAuthMutation,
} from '../../../../queries';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import housecallProLogo from '../../../assets/logo_housecallpro_banner.png';
import { Button } from '../../../components/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { TextInput } from '../../../components/TextInput';
import { background, nameCircles, white } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

const disconnectModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Disconnect',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

enum TabValues {
	Configure = 'configure',
	Manage = 'manage',
}

export function HousecallPro() {
	const [apiKey, setApiKey] = React.useState('');
	const [isEditing, setIsEditing] = React.useState(false);
	const [isDisconnectModalOpen, setIsDisconnectModalOpen] = React.useState(false);
	const history = useHistory();
	const userSession = useUserSession();
	const adminUserSession = userSession as AdminUserSessionContext;
	const params = useParams<{ tab: TabValues }>();

	const authMutation = usePostHousecallProAuthMutation({
		onSuccess: () => {
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.HousecallPro });
			reactQueryClient.invalidateQueries({ queryKey: [HOUSECALL_PRO_LOCATIONS_QUERY_KEY] });
			toast.success('Successfully connected to Housecall Pro.');
			setApiKey('');
			setIsEditing(false);
		},
	});

	const disconnectMutation = useDisconnectAccountFromIntegrationMutation({
		onError: error => {
			toast.error(error.systemMessage || 'Failed to disconnect Housecall Pro. Please try again.');
		},
		onSuccess: () => {
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.HousecallPro });
			reactQueryClient.invalidateQueries({ queryKey: [HOUSECALL_PRO_LOCATIONS_QUERY_KEY] });
			toast.success('Housecall Pro has been disconnected.');
		},
	});

	const onDisconnectModalRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		setIsDisconnectModalOpen(false);
		if (result?.isDestructive && !canceled) {
			disconnectMutation.mutate(Api.ExternalIntegrationType.HousecallPro);
		}
	};

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.HousecallPro,
	});

	const configuration = integrationQuery.data?.configuration as Api.IHousecallProIntegrationConfiguration;
	const isConnected = !!configuration?.accountConnection?.isConnected;
	const needsConfiguration = configuration?.needsConfiguration !== false;

	const changeTab = (value: string) => {
		history.replace({
			pathname: `/integrations/${Api.IntegrationProvider.HousecallPro}/${value}`,
		});
	};

	React.useEffect(() => {
		if (!integrationQuery.data) {
			return;
		}

		if (needsConfiguration) {
			if (adminUserSession.isAdmin) {
				changeTab(TabValues.Configure);
			} else {
				changeTab(TabValues.Manage);
			}
		} else {
			changeTab(TabValues.Manage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [needsConfiguration, adminUserSession.isAdmin, integrationQuery.data]);

	const onConnect = async () => {
		if (!apiKey.trim()) {
			toast.error('Please enter your Housecall Pro API key.');
			return;
		}
		authMutation.mutate({ apiKey: apiKey.trim() });
	};

	if (integrationQuery.isFetching || authMutation.isPending || disconnectMutation.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const hasAuthError = authMutation.error;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(styleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={housecallProLogo} alt='Housecall Pro Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with Housecall Pro, and pull client information, including names, email, phone number, and
				more.
			</p>

			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={params.tab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					{adminUserSession.isAdmin ? (
						<Tabs.Trigger
							value={TabValues.Configure}
							className={`tab-view-tabbar-item ${
								params.tab === TabValues.Configure ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<SuccessCheckIcon
								fillColor={needsConfiguration ? nameCircles : undefined}
								ringColor={needsConfiguration ? background : undefined}
							/>
							<span>Configure</span>
						</Tabs.Trigger>
					) : null}

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${
							params.tab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Configure} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						{needsConfiguration || isEditing ? (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									{needsConfiguration
										? 'To get started, enter an API key from your Housecall Pro account. You can find (or create) a key in the API Key Management app in your Housecall Pro Apps section.'
										: 'Enter a new API key to update your Housecall Pro connection.'}
								</p>
								<div className={css(styleSheet.signInContainer)}>
									<div className={css(styleSheet.signInForm)}>
										<div className={css(styleSheet.inputWrapper)}>
											<label htmlFor='housecall-pro-api-key' className={css(styleSheet.signInFormFieldLabel)}>
												API Key
											</label>
											<div className={css(styleSheet.signInFormField)}>
												<TextInput
													autoComplete='off'
													autoFocus={true}
													id='housecall-pro-api-key'
													onChange={ev => setApiKey(ev.target.value)}
													type='password'
													value={apiKey}
													className={css(styleSheet.textInput)}
													aria-invalid={!!hasAuthError}
													aria-describedby={hasAuthError ? 'housecall-pro-api-key-error' : undefined}
												/>
												{hasAuthError && <DangerIcon backgroundColor={white} />}
											</div>
											{hasAuthError ? (
												<span
													id='housecall-pro-api-key-error'
													role='alert'
													className={css(styleSheet.signInFormFieldError)}
												>
													{authMutation.error?.systemMessage || 'Unable to verify this API key. Please try again.'}
												</span>
											) : null}
										</div>
									</div>
									<div className={css(bs.horizontalStack)}>
										<Button onClick={onConnect} kind='primary'>
											{needsConfiguration ? 'Connect' : 'Save'}
										</Button>
										{isEditing ? (
											<Button
												onClick={() => {
													setIsEditing(false);
													setApiKey('');
													authMutation.reset();
												}}
												kind='reverse'
											>
												Cancel
											</Button>
										) : null}
									</div>
								</div>
							</>
						) : (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									Your Housecall Pro account is connected and configured to sync with Levitate.
								</p>
								<div className={css(bs.horizontalStack)}>
									<Button onClick={() => setIsEditing(true)} kind='primary'>
										Update API Key
									</Button>
									<Button onClick={() => setIsDisconnectModalOpen(true)} kind='reverse'>
										Disconnect
									</Button>
								</div>
							</>
						)}
					</div>
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							{isConnected
								? 'Your Housecall Pro data will show up in the contacts tab.'
								: 'Once you are configured, your Housecall Pro data will show up in the contacts tab.'}
						</p>
						<Link to='/people' className={css(bs.ctaButton)}>
							Contacts
						</Link>
					</div>
				</Tabs.Content>
			</Tabs.Root>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isDisconnectModalOpen,
					onRequestClose: onDisconnectModalRequestClose,
				}}
				options={disconnectModalOptions}
				title='Disconnect Housecall Pro? Levitate will stop syncing client data from your Housecall Pro account. You can reconnect at any time by entering your API key again.'
			/>
		</div>
	);
}
